<script lang="ts">
  import { onMount } from 'svelte';
  import { applySavedThemeFields, profile, theme } from '../stores/editor';
  import { apiClient } from '@/utils/api';

  type TemplateDefaults = {
    handle_color: string;
    name_color: string;
    tagline_color: string;
    accent_color: string;
    handle_size: number;
    name_size: number | null;
    tagline_size: number;
  };

  type TemplateCapabilities = {
    name_style: boolean;
    accent: boolean;
    link_count: boolean;
  };

  type TemplateMeta = {
    key: string;
    name: string;
    mood: string;
    layout: string;
    defaults: TemplateDefaults | null;
    capabilities: TemplateCapabilities | null;
  };

  // Used before the template list has loaded (or for the "auto" entry,
  // whose defaults/capabilities are null) so the style controls always
  // have something sane to show.
  const FALLBACK_DEFAULTS: TemplateDefaults = {
    handle_color: '#ffffff',
    name_color: '#a1a1aa',
    tagline_color: '#a1a1aa',
    accent_color: '#ffffff',
    handle_size: 96,
    name_size: 32,
    tagline_size: 24,
  };
  const FALLBACK_CAPABILITIES: TemplateCapabilities = { name_style: true, accent: false, link_count: true };

  // ---- State ----
  const initOg = ($theme as any)?.og_settings || {};
  let selected = $state<string>(($theme as any)?.og_template || 'auto');

  // Content overrides
  let ogDisplayName = $state<string>(initOg.display_name || '');
  let ogTagline = $state<string>(initOg.tagline || '');
  let ogSubtitle = $state<string>(initOg.subtitle || '');

  // Per-element style overrides. `null` means "not touched — show the
  // selected template's default as a placeholder, send nothing on save".
  let handleColor = $state<string | null>(initOg.handle_color ?? null);
  let handleSize = $state<number | null>(initOg.handle_size ?? null);
  let nameColor = $state<string | null>(initOg.name_color ?? null);
  let nameSize = $state<number | null>(initOg.name_size ?? null);
  let taglineColor = $state<string | null>(initOg.tagline_color ?? null);
  let taglineSize = $state<number | null>(initOg.tagline_size ?? null);
  let ogAccentColor = $state<string | null>(initOg.accent_color ?? null);

  // UI state
  let templates = $state<TemplateMeta[]>([]);
  let templatesLoading = $state(true);
  let templatesError = $state<string | null>(null);
  let saving = $state(false);
  let savedAt = $state<number | null>(null);
  let error = $state<string | null>(null);
  let editingElement = $state<string | null>(null);

  // Drives preview cache busting. Bumped on debounced edits + after save.
  let previewKey = $state<number>(Date.now());
  let previewLoading = $state(true);
  let previewError = $state(false);

  const handle = $derived($profile?.username || '');
  const sel = $derived(templates.find((t) => t.key === selected) ?? null);

  // "auto" 카드가 선택돼 있을 때 실제로 그려지는 템플릿 — 서버가 저장된
  // page_theme.template 으로부터 해석해 내려준 값(og_template_effective).
  // 지금 폼에서 auto 로 바꿔도(아직 저장 전) 이 값은 안 바뀐다 — 미리보기는
  // 어차피 서버가 실제 page_theme 을 보고 다시 해석하므로 라벨용일 뿐이다.
  const effectiveTemplateKey = $derived(
    selected === 'auto' ? (($theme as any)?.og_template_effective as string | undefined) : selected,
  );
  const effectiveMeta = $derived(templates.find((t) => t.key === effectiveTemplateKey) ?? null);
  const defaults = $derived(effectiveMeta?.defaults ?? FALLBACK_DEFAULTS);
  const capabilities = $derived(effectiveMeta?.capabilities ?? FALLBACK_CAPABILITIES);

  const savedTemplate = ($theme as any)?.og_template || 'auto';
  const dirty = $derived(
    selected !== savedTemplate ||
      ogDisplayName !== (initOg.display_name || '') ||
      ogTagline !== (initOg.tagline || '') ||
      ogSubtitle !== (initOg.subtitle || '') ||
      handleColor !== (initOg.handle_color ?? null) ||
      handleSize !== (initOg.handle_size ?? null) ||
      nameColor !== (initOg.name_color ?? null) ||
      nameSize !== (initOg.name_size ?? null) ||
      taglineColor !== (initOg.tagline_color ?? null) ||
      taglineSize !== (initOg.tagline_size ?? null) ||
      ogAccentColor !== (initOg.accent_color ?? null),
  );

  // Build the JSON payload sent to the backend. Only keys the user actually
  // touched are sent — an untouched key means "use the template's own
  // default", and the server/renderer must see that as absence, not as a
  // value equal to some hardcoded default (2026-09-17 감사 §6-3·4).
  function currentSettingsPayload(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (handleColor !== null) out.handle_color = handleColor;
    if (handleSize !== null) out.handle_size = handleSize;
    if (nameColor !== null) out.name_color = nameColor;
    if (nameSize !== null) out.name_size = nameSize;
    if (taglineColor !== null) out.tagline_color = taglineColor;
    if (taglineSize !== null) out.tagline_size = taglineSize;
    if (ogAccentColor !== null) out.accent_color = ogAccentColor;
    if (ogDisplayName) out.display_name = ogDisplayName;
    if (ogTagline) out.tagline = ogTagline;
    if (ogSubtitle) out.subtitle = ogSubtitle;
    return out;
  }

  // Build the preview URL — points at the SAME endpoint that ships the
  // shared OG image, just with preview overrides. Guarantees byte-equal
  // preview ↔ shared image (drift = 0).
  const previewUrl = $derived.by(() => {
    if (!handle) return null;
    const params = new URLSearchParams();
    params.set('v', String(previewKey));
    params.set('preview_template', selected);
    params.set('preview_settings', JSON.stringify(currentSettingsPayload()));
    return `/og/${handle}.png?${params.toString()}`;
  });

  // ---- Debounce ----
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  function schedulePreviewRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      previewLoading = true;
      previewError = false;
      previewKey = Date.now();
    }, 500);
  }

  // Reactive trigger: any edit-time field change schedules a refresh.
  // (Reading these here registers the dependency; the actual side-effect
  // is the debounced timer.)
  $effect(() => {
    void selected;
    void ogDisplayName;
    void ogTagline;
    void ogSubtitle;
    void ogAccentColor;
    void handleColor;
    void handleSize;
    void nameColor;
    void nameSize;
    void taglineColor;
    void taglineSize;
    schedulePreviewRefresh();
  });

  // ---- Templates fetch ----
  async function loadTemplates() {
    templatesLoading = true;
    templatesError = null;
    try {
      const res = await apiClient.get<{ templates: TemplateMeta[] }>(
        '/api/v1/og_templates',
        { skipCaseConversion: true },
      );
      if (res.success && res.data?.templates) {
        templates = res.data.templates;
      } else {
        templatesError = '템플릿 목록을 불러오지 못했습니다.';
      }
    } catch (e) {
      templatesError = (e as Error)?.message || '템플릿 목록을 불러오지 못했습니다.';
    } finally {
      templatesLoading = false;
    }
  }

  onMount(() => {
    loadTemplates();
  });

  function toggleEdit(element: string) {
    editingElement = editingElement === element ? null : element;
  }

  function resetElementDefaults() {
    handleColor = null;
    handleSize = null;
    nameColor = null;
    nameSize = null;
    taglineColor = null;
    taglineSize = null;
    ogAccentColor = null;
  }

  function retryPreview() {
    previewError = false;
    previewLoading = true;
    previewKey = Date.now();
  }

  async function save() {
    if (!dirty || saving) return;
    saving = true;
    error = null;
    try {
      const ogSettings = currentSettingsPayload();
      const res = await apiClient.patch<{ profile: any }>(
        '/api/v1/my_profile',
        { profile: { page_theme: { og_template: selected, og_settings: ogSettings } } },
        { skipCaseConversion: true },
      );
      if (res.success) {
        const savedFields: Record<string, unknown> = { og_template: selected, og_settings: ogSettings };
        const effective = res.data?.profile?.page_theme?.og_template_effective;
        if (effective) savedFields.og_template_effective = effective;
        applySavedThemeFields(savedFields as any);
        savedAt = Date.now();
        // Refresh preview from the freshly-saved state.
        previewLoading = true;
        previewError = false;
        previewKey = Date.now();
      } else {
        error = '저장에 실패했습니다.';
      }
    } catch (e) {
      error = (e as Error)?.message || '저장 중 오류가 발생했습니다.';
    } finally {
      saving = false;
    }
  }
</script>

<div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
  <!-- Left: Editor -->
  <section class="space-y-6">
    <header class="rounded-3xl border border-default bg-surface p-6">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-600 dark:text-sky-400">OG Image</p>
      <h2 class="mt-2 text-2xl font-semibold text-content">소셜 공유 이미지</h2>
      <p class="mt-2 text-sm leading-6 text-content-secondary">
        카카오톡 / 인스타 / 트위터 등에서 프로필 링크를 공유할 때 보여지는 1200x630 이미지입니다.
        편집하면 우측 미리보기가 실제 렌더러로 자동 갱신됩니다.
      </p>

      <div class="mt-5 grid gap-4 sm:grid-cols-2">
        <div class="rounded-2xl border border-default bg-surface-tertiary p-3">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">handle (자동)</div>
          <div class="mt-1 font-mono text-sm text-content">@{handle || 'handle'}</div>
        </div>
        <label class="grid gap-1 rounded-2xl border border-default bg-surface-tertiary p-3">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">표시 이름</span>
          <input type="text" class="input text-sm" placeholder={$profile?.display_name || '프로필 이름 사용'} bind:value={ogDisplayName} />
        </label>
        <label class="grid gap-1 rounded-2xl border border-default bg-surface-tertiary p-3">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">태그라인</span>
          <input type="text" class="input text-sm" placeholder={($profile?.bio || '').split('\n')[0] || '한 줄 소개'} bind:value={ogTagline} />
        </label>
        <label class="grid gap-1 rounded-2xl border border-default bg-surface-tertiary p-3">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">부가 문구</span>
          <input type="text" class="input text-sm" placeholder="예: AI 게이트웨이" bind:value={ogSubtitle} />
        </label>
      </div>
    </header>

    <!-- Template picker -->
    <div class="rounded-3xl border border-default bg-surface p-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold text-content">템플릿</h3>
        <span class="text-xs text-content-secondary">선택: <b class="font-mono text-content">{selected}</b></span>
      </div>

      {#if templatesLoading}
        <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {#each Array(6) as _}
            <div class="aspect-[1200/630] animate-pulse rounded-xl bg-surface-tertiary"></div>
          {/each}
        </div>
      {:else if templatesError}
        <div class="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-300">
          {templatesError}
          <button type="button" class="ml-2 underline" onclick={loadTemplates}>다시 시도</button>
        </div>
      {:else}
        <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {#each templates as t (t.key)}
            <button
              type="button"
              class={`group rounded-2xl border p-2 text-left transition ${
                selected === t.key
                  ? 'border-gray-950 bg-surface-tertiary ring-2 ring-gray-950 ring-offset-2 dark:border-gray-200 dark:ring-gray-200'
                  : 'border-default bg-surface hover:border-gray-400 dark:hover:border-gray-500'
              }`}
              onclick={() => (selected = t.key)}
            >
              <div class="flex aspect-[1200/630] items-center justify-center rounded-xl bg-surface-tertiary">
                <span class="font-mono text-xs text-content-tertiary">{t.layout}</span>
              </div>
              <div class="mt-2 px-1">
                <div class="truncate text-xs font-semibold text-content">{t.name}</div>
                <div class="truncate text-[11px] text-content-secondary">{t.mood}</div>
              </div>
            </button>
          {/each}
        </div>
        {#if selected === 'auto' && effectiveMeta}
          <p class="mt-3 text-xs text-content-secondary">
            지금은 <b class="text-content">"{effectiveMeta.name}"</b> 으로 그려집니다 — 페이지 테마를 바꾸면 카드도 따라갑니다.
          </p>
        {/if}
      {/if}

      <div class="mt-6 flex items-center justify-between gap-2">
        <div class="text-xs text-content-secondary">
          {#if error}
            <span class="text-rose-600">{error}</span>
          {:else if savedAt}
            <span class="text-emerald-600">저장됨</span>
          {:else if dirty}
            <span>변경사항 있음 — 미리보기는 자동 갱신, 저장하면 실제 OG에 반영</span>
          {/if}
        </div>
        <div class="flex items-center gap-2">
          <button type="button" class="btn btn-secondary btn-sm" onclick={() => (selected = 'auto')}>
            기본값으로
          </button>
          <button
            type="button"
            disabled={!dirty || saving}
            class="btn btn-sm bg-gray-950 text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
            onclick={save}
          >
            {saving ? '저장 중…' : '저장하기'}
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Right: Sticky Preview + Element Editors -->
  <aside class="hidden xl:block">
    <div class="sticky top-6 space-y-4">
      <div class="rounded-3xl border border-default bg-surface p-5">
        <div class="mb-3 flex items-center justify-between">
          <span class="text-xs font-semibold uppercase tracking-wider text-content-secondary">프리뷰 (실제 렌더러)</span>
          <span class="text-[11px] text-content-tertiary">1200 × 630</span>
        </div>
        <div class="relative overflow-hidden rounded-xl border border-default bg-surface-tertiary" style="aspect-ratio: 1200 / 630;">
          {#if previewUrl}
            <img
              src={previewUrl}
              alt={`OG preview${sel ? ` — ${sel.name}` : ''}`}
              width="1200"
              height="630"
              class="block h-full w-full"
              style="background: #0A0A0A;"
              onload={() => {
                previewLoading = false;
                previewError = false;
              }}
              onerror={() => {
                previewLoading = false;
                previewError = true;
              }}
            />
            {#if previewLoading}
              <div class="absolute inset-0 flex items-center justify-center bg-black/30 text-xs text-white">
                렌더링 중…
              </div>
            {/if}
            {#if previewError}
              <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-sm text-white">
                <span>미리보기 로드 실패</span>
                <button type="button" class="rounded bg-white/20 px-3 py-1 text-xs hover:bg-white/30" onclick={retryPreview}>다시 시도</button>
              </div>
            {/if}
          {:else}
            <div class="absolute inset-0 flex items-center justify-center text-xs text-content-tertiary">
              사용자명을 설정하면 미리보기가 표시됩니다.
            </div>
          {/if}
        </div>
        {#if sel}
          <div class="mt-3 flex items-center justify-between text-center">
            <div>
              <div class="text-sm font-semibold text-content">{sel.name}</div>
              <div class="text-xs text-content-secondary">{sel.mood}</div>
            </div>
            {#if previewUrl}
              <a class="text-[11px] font-mono text-sky-600 hover:underline" href={previewUrl} target="_blank" rel="noreferrer">새 창 ↗</a>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Element Style Editors -->
      <div class="space-y-4 rounded-3xl border border-default bg-surface p-5">
        <div class="flex items-center justify-between">
          <h4 class="text-sm font-semibold text-content">요소별 스타일</h4>
          <button type="button" class="text-[11px] text-content-tertiary hover:text-content-secondary" onclick={resetElementDefaults}>기본값으로</button>
        </div>

        <!-- Handle style -->
        <div class="rounded-2xl border border-default p-3 {editingElement === 'handle' ? 'ring-2 ring-primary/30' : ''}">
          <button type="button" class="flex w-full items-center justify-between" onclick={() => toggleEdit('handle')}>
            <div class="flex items-center gap-2">
              <div class="h-4 w-4 rounded-full border border-default" style="background: {handleColor ?? defaults.handle_color};"></div>
              <span class="text-xs font-semibold text-content">@handle</span>
              <span class="text-[10px] text-content-tertiary">{handleSize ?? defaults.handle_size}px</span>
            </div>
            <svg class="h-4 w-4 text-content-tertiary transition-transform {editingElement === 'handle' ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {#if editingElement === 'handle'}
            <div class="mt-3 grid grid-cols-2 gap-3">
              <label class="grid gap-1">
                <span class="text-[10px] text-content-tertiary">색상</span>
                <div class="flex items-center gap-2">
                  <input type="color" class="h-8 w-8 cursor-pointer rounded-lg border border-default" value={handleColor ?? defaults.handle_color} oninput={(e) => (handleColor = (e.currentTarget as HTMLInputElement).value)} />
                  <input type="text" class="input flex-1 font-mono text-xs" value={handleColor ?? defaults.handle_color} oninput={(e) => (handleColor = (e.currentTarget as HTMLInputElement).value)} maxlength="7" />
                </div>
              </label>
              <label class="grid gap-1">
                <span class="text-[10px] text-content-tertiary">크기 ({handleSize ?? defaults.handle_size}px)</span>
                <input type="range" min="48" max="140" step="4" value={handleSize ?? defaults.handle_size} oninput={(e) => (handleSize = Number((e.currentTarget as HTMLInputElement).value))} class="mt-1" />
              </label>
            </div>
          {/if}
        </div>

        <!-- Name style -->
        {#if capabilities.name_style}
          <div class="rounded-2xl border border-default p-3 {editingElement === 'name' ? 'ring-2 ring-primary/30' : ''}">
            <button type="button" class="flex w-full items-center justify-between" onclick={() => toggleEdit('name')}>
              <div class="flex items-center gap-2">
                <div class="h-4 w-4 rounded-full border border-default" style="background: {nameColor ?? defaults.name_color};"></div>
                <span class="text-xs font-semibold text-content">표시 이름</span>
                <span class="text-[10px] text-content-tertiary">{nameSize ?? defaults.name_size ?? 32}px</span>
              </div>
              <svg class="h-4 w-4 text-content-tertiary transition-transform {editingElement === 'name' ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {#if editingElement === 'name'}
              <div class="mt-3 grid grid-cols-2 gap-3">
                <label class="grid gap-1">
                  <span class="text-[10px] text-content-tertiary">색상</span>
                  <div class="flex items-center gap-2">
                    <input type="color" class="h-8 w-8 cursor-pointer rounded-lg border border-default" value={nameColor ?? defaults.name_color} oninput={(e) => (nameColor = (e.currentTarget as HTMLInputElement).value)} />
                    <input type="text" class="input flex-1 font-mono text-xs" value={nameColor ?? defaults.name_color} oninput={(e) => (nameColor = (e.currentTarget as HTMLInputElement).value)} maxlength="7" />
                  </div>
                </label>
                <label class="grid gap-1">
                  <span class="text-[10px] text-content-tertiary">크기 ({nameSize ?? defaults.name_size ?? 32}px)</span>
                  <input type="range" min="16" max="64" step="2" value={nameSize ?? defaults.name_size ?? 32} oninput={(e) => (nameSize = Number((e.currentTarget as HTMLInputElement).value))} class="mt-1" />
                </label>
              </div>
            {/if}
          </div>
        {:else}
          <p class="rounded-2xl border border-default bg-surface-tertiary p-3 text-[11px] text-content-tertiary">
            이 템플릿은 표시 이름을 그리지 않습니다 — 스타일 컨트롤이 없습니다.
          </p>
        {/if}

        <!-- Tagline style -->
        <div class="rounded-2xl border border-default p-3 {editingElement === 'tagline' ? 'ring-2 ring-primary/30' : ''}">
          <button type="button" class="flex w-full items-center justify-between" onclick={() => toggleEdit('tagline')}>
            <div class="flex items-center gap-2">
              <div class="h-4 w-4 rounded-full border border-default" style="background: {taglineColor ?? defaults.tagline_color};"></div>
              <span class="text-xs font-semibold text-content">태그라인</span>
              <span class="text-[10px] text-content-tertiary">{taglineSize ?? defaults.tagline_size}px</span>
            </div>
            <svg class="h-4 w-4 text-content-tertiary transition-transform {editingElement === 'tagline' ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {#if editingElement === 'tagline'}
            <div class="mt-3 grid grid-cols-2 gap-3">
              <label class="grid gap-1">
                <span class="text-[10px] text-content-tertiary">색상</span>
                <div class="flex items-center gap-2">
                  <input type="color" class="h-8 w-8 cursor-pointer rounded-lg border border-default" value={taglineColor ?? defaults.tagline_color} oninput={(e) => (taglineColor = (e.currentTarget as HTMLInputElement).value)} />
                  <input type="text" class="input flex-1 font-mono text-xs" value={taglineColor ?? defaults.tagline_color} oninput={(e) => (taglineColor = (e.currentTarget as HTMLInputElement).value)} maxlength="7" />
                </div>
              </label>
              <label class="grid gap-1">
                <span class="text-[10px] text-content-tertiary">크기 ({taglineSize ?? defaults.tagline_size}px)</span>
                <input type="range" min="14" max="48" step="2" value={taglineSize ?? defaults.tagline_size} oninput={(e) => (taglineSize = Number((e.currentTarget as HTMLInputElement).value))} class="mt-1" />
              </label>
            </div>
          {/if}
        </div>

        <!-- Accent style — only templates whose layout actually paints an accent (terminal) -->
        {#if capabilities.accent}
          <div class="rounded-2xl border border-default p-3 {editingElement === 'accent' ? 'ring-2 ring-primary/30' : ''}">
            <button type="button" class="flex w-full items-center justify-between" onclick={() => toggleEdit('accent')}>
              <div class="flex items-center gap-2">
                <div class="h-4 w-4 rounded-full border border-default" style="background: {ogAccentColor ?? defaults.accent_color};"></div>
                <span class="text-xs font-semibold text-content">강조색</span>
              </div>
              <svg class="h-4 w-4 text-content-tertiary transition-transform {editingElement === 'accent' ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {#if editingElement === 'accent'}
              <div class="mt-3 grid grid-cols-1 gap-3">
                <label class="grid gap-1">
                  <span class="text-[10px] text-content-tertiary">색상</span>
                  <div class="flex items-center gap-2">
                    <input type="color" class="h-8 w-8 cursor-pointer rounded-lg border border-default" value={ogAccentColor ?? defaults.accent_color} oninput={(e) => (ogAccentColor = (e.currentTarget as HTMLInputElement).value)} />
                    <input type="text" class="input flex-1 font-mono text-xs" value={ogAccentColor ?? defaults.accent_color} oninput={(e) => (ogAccentColor = (e.currentTarget as HTMLInputElement).value)} maxlength="7" />
                  </div>
                </label>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </aside>
</div>
